import { useVersioning } from '@/data/context/VersioningContext';
import Activity from '@/models/Activity';
import { FeedbackCreateDTO } from '@/models/Feedback';
import { Question } from '@/models/Question';
import { resolveQuestionInActivity } from '@/utils/questions/resolveQuestionInActivity';
import {
  toClonedChildren,
  toClonedFeedbacks,
} from '@/utils/questions/toClonedChildren';

type WithId = { id?: number };

type QuestionChanges = { feedbacks?: FeedbackCreateDTO[] };

type UpdateQuestion<C, T> = (
  activityId: number,
  questionId: number,
  changes: C,
) => Promise<T>;

export default function useEditableQuestion(activity: Activity) {
  const { ensureEditable } = useVersioning<Activity>();

  const resolveEditable = async <T extends Question>(question: T) => {
    const editableActivity = await ensureEditable();
    const target =
      editableActivity.id === activity.id
        ? question
        : resolveQuestionInActivity(editableActivity, question);

    const toClone = <C extends WithId>(
      items: C[],
      select: (question: T) => WithId[] | undefined,
    ) => toClonedChildren(items, select(question) ?? [], select(target) ?? []);

    const updateTarget = async <C extends QuestionChanges>(
      changes: C,
      update: UpdateQuestion<C, T>,
    ): Promise<T> => {
      if (!Object.keys(changes).length) return target;
      if (!changes.feedbacks)
        return update(editableActivity.id, target.id, changes);
      const feedbacks = toClonedFeedbacks(
        changes.feedbacks,
        question.feedbacks ?? [],
        target.feedbacks ?? [],
      );
      return update(editableActivity.id, target.id, {
        ...changes,
        feedbacks,
      });
    };

    return { editableActivity, target, toClone, updateTarget };
  };

  return { ensureEditable, resolveEditable };
}
